import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import Dialog from "../../components/ui/Dialog";
import { useJobTitles, useRoles } from "../../api/queries/organizationQueries";
import {
  useActivateTemplate,
  useArchiveTemplate,
  useDeleteTemplate,
  useNewTemplateVersion,
  useTemplates,
} from "../../api/queries/performanceQueries";
import { getApiErrorMessage, getApiErrorStatus } from "../../utils/apiError";
import { formatDateTime, fromNow } from "../../utils/dateTime";
import TemplateStatusBadge from "./components/TemplateStatusBadge";
import { EVALUATION_TYPES } from "./components/templateUtils";
import "./performance.css";
import { useCanModify } from "../../hooks/usePermissions";
import usePermissions from "../../hooks/usePermissions";

const EMPTY = [];

/** One group per code: versions newest first; the row shows the ACTIVE one (or the latest). */
const groupByCode = (templates) => {
  const map = new Map();
  templates.forEach((t) => {
    if (!map.has(t.code)) map.set(t.code, []);
    map.get(t.code).push(t);
  });
  return [...map.entries()].map(([code, versions]) => {
    const sorted = [...versions].sort((a, b) => b.version - a.version);
    return {
      code,
      versions: sorted,
      primary: sorted.find((v) => v.status === "ACTIVE") || sorted[0],
      draft: sorted.find((v) => v.status === "DRAFT") || null,
    };
  });
};

const CONFIRM = {
  activate: {
    title: (t) => `Activate ${t.code} v${t.version}?`,
    body: "Activating replaces the current active version for new evaluations. Existing evaluations keep their form.",
    button: "Activate",
    variant: "success",
    done: (t) => `${t.code} v${t.version} is now active.`,
  },
  archive: {
    title: (t) => `Archive ${t.code} v${t.version}?`,
    body: "No new evaluations can use it. Evaluations already started are not affected.",
    button: "Archive",
    variant: "warning",
    done: (t) => `${t.code} v${t.version} archived.`,
  },
  delete: {
    title: (t) => `Delete draft ${t.code} v${t.version}?`,
    body: "This can't be undone.",
    button: "Delete",
    variant: "danger",
    done: (t) => `Draft ${t.code} v${t.version} deleted.`,
  },
};

/** Who the template is for: role / job title chips, or "Anyone". */
const AppliesTo = ({ appliesTo, roleName, jobTitleName }) => {
  const roles = appliesTo?.roleIds || [];
  const titles = appliesTo?.jobTitleIds || [];
  if (!roles.length && !titles.length)
    return <span className="text-slate-400">Anyone</span>;
  return (
    <div className="d-flex flex-wrap gap-1">
      {roles.map((r) => (
        <span key={r} className="chip chip--sm">
          {roleName(r)}
        </span>
      ))}
      {titles.map((j) => (
        <span key={j} className="chip chip--sm chip--alt">
          {jobTitleName(j)}
        </span>
      ))}
    </div>
  );
};

/** Actions allowed for one version's status. */
const VersionActions = ({
  template: t,
  hasDraft,
  onConfirm,
  onNewVersion,
  busy,
}) => {
  const newVersionBlocked = hasDraft
    ? "A draft already exists for this code"
    : undefined;
  if (t.status === "DRAFT") {
    return (
      <div className="d-inline-flex gap-1">
        <Link
          to={`/performance/templates/${t.id}/edit`}
          className="btn btn-sm btn-outline-primary perf-action-btn"
        >
          <i className="bi bi-pencil-square"></i> Edit
        </Link>
        <button
          type="button"
          className="btn btn-sm btn-outline-success perf-action-btn"
          onClick={() => onConfirm("activate", t)}
          disabled={busy}
        >
          Activate
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-danger perf-action-btn"
          onClick={() => onConfirm("delete", t)}
          disabled={busy}
          aria-label={`Delete ${t.code} v${t.version}`}
        >
          <i className="bi bi-trash"></i>
        </button>
      </div>
    );
  }
  return (
    <div className="d-inline-flex gap-1">
      <Link
        to={`/performance/templates/${t.id}`}
        className="btn btn-sm btn-light border perf-action-btn"
      >
        View
      </Link>
      <button
        type="button"
        className="btn btn-sm btn-outline-primary perf-action-btn"
        onClick={() => onNewVersion(t)}
        disabled={busy || hasDraft}
        title={newVersionBlocked}
      >
        {t.status === "ARCHIVED" ? "New version from this" : "New version"}
      </button>
      {t.status === "ACTIVE" && (
        <button
          type="button"
          className="btn btn-sm btn-outline-warning perf-action-btn"
          onClick={() => onConfirm("archive", t)}
          disabled={busy}
        >
          Archive
        </button>
      )}
    </div>
  );
};

const TemplatesPage = () => {
  const canModify = useCanModify("performance", { company: true });
  const navigate = useNavigate();
  // HR-level (performance with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("performance");
  const query = useTemplates();
  const { data: roles = EMPTY } = useRoles();
  const { data: jobTitles = EMPTY } = useJobTitles();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null); // { action, template }

  const activate = useActivateTemplate();
  const archive = useArchiveTemplate();
  const remove = useDeleteTemplate();
  const newVersion = useNewTemplateVersion();
  const busy =
    activate.isPending ||
    archive.isPending ||
    remove.isPending ||
    newVersion.isPending;

  const roleName = useMemo(() => {
    const m = new Map(roles.map((r) => [r.id, r.title || r.name]));
    return (id) => m.get(id) || "Unknown role";
  }, [roles]);
  const jobTitleName = useMemo(() => {
    const m = new Map(jobTitles.map((j) => [j.id, j.title || j.name]));
    return (id) => m.get(id) || "Unknown job title";
  }, [jobTitles]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groupByCode(query.data || EMPTY).filter(
      ({ code, primary, versions }) =>
        (!q ||
          code.toLowerCase().includes(q) ||
          (primary.name || "").toLowerCase().includes(q)) &&
        (!status || versions.some((v) => v.status === status)) &&
        (!type || primary.evaluationType === type),
    );
  }, [query.data, search, status, type]);

  const toggle = (code) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });

  const onError = (fallback) => (err) => {
    const msg = getApiErrorMessage(err, fallback);
    if (getApiErrorStatus(err) === 409) toast.info(msg);
    else toast.error(msg);
  };

  const runConfirm = () => {
    const { action, template } = confirm;
    const mutation = { activate, archive, delete: remove }[action];
    mutation.mutate(template.id, {
      onSuccess: () => {
        toast.success(CONFIRM[action].done(template));
        setConfirm(null);
      },
      onError: (err) => {
        onError(`Could not ${action} the template.`)(err);
        setConfirm(null);
      },
    });
  };

  const startNewVersion = (t) =>
    newVersion.mutate(t.id, {
      onSuccess: (draft) => {
        toast.success(`Draft ${draft.code} v${draft.version} created.`);
        navigate(`/performance/templates/${draft.id}/edit`);
      },
      onError: onError("Could not create a new version."),
    });

  if (!isCompanyWide) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          <div className="builder-card approval-empty">
            <i className="bi bi-shield-lock"></i>Only Admin and HR can manage
            evaluation templates.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">
              Evaluation Templates
            </h1>
            <p className="text-muted small mb-0">
              Forms used for performance evaluations. Each code keeps its
              versions.
            </p>
          </div>
          {canModify && (
            <Link
              to="/performance/templates/new"
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
            >
              <i className="bi bi-plus-lg me-2"></i> New Template
            </Link>
          )}
        </div>

        <div className="perf-filters mb-3">
          <div className="position-relative perf-filter-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Search code or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search code or name"
            />
          </div>
          <select
            className="status-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Status"
          >
            <option value="">All statuses</option>
            <option value="ACTIVE">Has active version</option>
            <option value="DRAFT">Has draft</option>
            <option value="ARCHIVED">Has archived</option>
          </select>
          <select
            className="status-select"
            value={type}
            onChange={(e) => setType(e.target.value)}
            aria-label="Evaluation type"
          >
            <option value="">All types</option>
            {EVALUATION_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div className="glass-table-card">
          {query.isError ? (
            <div className="approval-empty text-danger">
              <i className="bi bi-exclamation-triangle"></i>
              {getApiErrorMessage(query.error, "Could not load templates.")}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table mb-0 perf-table">
                <thead className="custom-table-header">
                  <tr>
                    <th
                      className="ps-4 py-3 perf-col-expand"
                      aria-label="Expand"
                    ></th>
                    <th className="py-3">Code</th>
                    <th className="py-3">Name</th>
                    <th className="py-3">Type</th>
                    <th className="py-3">Applies to</th>
                    <th className="py-3">Version</th>
                    <th className="py-3">Status</th>
                    <th className="py-3">Updated</th>
                    <th className="py-3 pe-4 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {groups.map(({ code, versions, primary, draft }) => {
                    const isOpen = expanded.has(code);
                    return (
                      <React.Fragment key={code}>
                        <tr className={isOpen ? "perf-row-open" : ""}>
                          <td className="ps-4">
                            {versions.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-light border btn-icon-28"
                                onClick={() => toggle(code)}
                                aria-expanded={isOpen}
                                aria-label={`${isOpen ? "Hide" : "Show"} all versions of ${code}`}
                              >
                                <i
                                  className={`bi bi-chevron-${isOpen ? "down" : "right"}`}
                                ></i>
                              </button>
                            )}
                          </td>
                          <td className="font-monospace fw-semibold">{code}</td>
                          <td>
                            <div className="emp-name-text">{primary.name}</div>
                            {primary.description && (
                              <div className="emp-sub-text perf-desc">
                                {primary.description}
                              </div>
                            )}
                          </td>
                          <td>{primary.evaluationType}</td>
                          <td>
                            <AppliesTo
                              appliesTo={primary.appliesTo}
                              roleName={roleName}
                              jobTitleName={jobTitleName}
                            />
                          </td>
                          <td className="text-nowrap">
                            v{primary.version}
                            {draft && draft.id !== primary.id && (
                              <div className="fs-070 text-warning-emphasis">
                                Draft v{draft.version} in progress
                              </div>
                            )}
                          </td>
                          <td>
                            <TemplateStatusBadge status={primary.status} />
                          </td>
                          <td
                            className="text-nowrap"
                            title={formatDateTime(primary.updatedAt)}
                          >
                            {fromNow(primary.updatedAt)}
                          </td>
                          <td className="pe-4 text-end text-nowrap">
                            {canModify && (
                              <VersionActions
                                template={primary}
                                hasDraft={!!draft}
                                onConfirm={(action, t) =>
                                  setConfirm({ action, template: t })
                                }
                                onNewVersion={startNewVersion}
                                busy={busy}
                              />
                            )}
                          </td>
                        </tr>
                        {isOpen &&
                          versions.map((v) => (
                            <tr key={v.id} className="perf-version-row">
                              <td></td>
                              <td colSpan={4} className="text-slate-600">
                                <i className="bi bi-arrow-return-right me-2 text-slate-400"></i>
                                {v.name}
                              </td>
                              <td>v{v.version}</td>
                              <td>
                                <TemplateStatusBadge status={v.status} />
                              </td>
                              <td
                                className="text-nowrap"
                                title={formatDateTime(v.updatedAt)}
                              >
                                {fromNow(v.updatedAt)}
                              </td>
                              <td className="pe-4 text-end text-nowrap">
                                {canModify && (
                                  <VersionActions
                                    template={v}
                                    hasDraft={!!draft}
                                    onConfirm={(action, t) =>
                                      setConfirm({ action, template: t })
                                    }
                                    onNewVersion={startNewVersion}
                                    busy={busy}
                                  />
                                )}
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })}
                  {!query.isLoading && groups.length === 0 && (
                    <tr>
                      <td colSpan={9} className="approval-empty">
                        <i className="bi bi-clipboard2-data"></i>
                        {query.data?.length
                          ? "No templates match these filters."
                          : "No templates yet. Create one to start evaluating."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {query.isLoading && (
            <div className="text-center py-5">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            </div>
          )}
        </div>
      </div>

      {confirm && (
        <Dialog
          title={CONFIRM[confirm.action].title(confirm.template)}
          onClose={() => setConfirm(null)}
          busy={busy}
          footer={
            <>
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setConfirm(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn btn-${CONFIRM[confirm.action].variant} profile-btn`}
                onClick={runConfirm}
                disabled={busy}
              >
                {busy && (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  ></span>
                )}
                {CONFIRM[confirm.action].button}
              </button>
            </>
          }
        >
          <p className="fs-085 mb-0">{CONFIRM[confirm.action].body}</p>
        </Dialog>
      )}
    </Layout>
  );
};

export default TemplatesPage;
